import React, { useState, useRef, useEffect, useMemo } from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { useParams } from "react-router-dom";

import Modal from "../../modal/Modal";
import ConfirmModal from "../../modal/ConfirmModal";
import Input from "../../form-item/Input";
import Toggle from "../../form-item/Toggle";
import StatusBadge from "../../status-badge/StatusBadge";
import StatusIndicator from "../../status-indicator/StatusIndicator";
import CollapsedAvatarStack from "../../avatar-stack/CollapsedAvatarStack";
import MemberGroupSection from "../../member-list/MemberGroupSection";
import { MemberIndentSpacer } from "../../member-list/MemberGroupList";
import UserListRow from "../../user-item/UserListRow";
import {
    membershipsApi,
    useGetAllProjectMembershipsQuery,
    useGetProjectMembershipQuery,
    useUpdateMembershipMutation,
    useRemoveMembershipMutation,
} from "../../../app/accounts/memberships";
import { useGetCurrentUserQuery } from "../../../app/accounts/authuser";
import {
    useGetInvitationsQuery,
    useSendInvitationMutation,
    useResendInvitationMutation,
    useCancelInvitationMutation,
} from "../../../app/services/invitations";
import SortableUserItem from "./SortableUserItem";
import IpRestrictionModal from "./IpRestrictionModal";
import MemberFollowButton from "./MemberFollowButton";
import DropdownMenu from "../../dropdown-menu/DropdownMenu";
import {
    isClientGroupPosition as isClientGroup,
    isProducerGroupPosition as isProducerGroup,
    isSpecialistPosition,
    isPrimaryPosition,
    canControlMembers,
    shouldShowSpecialists,
    isFollowableMember,
    CLIENT_GROUP_POSITION_ORDER,
    PRODUCER_GROUP_POSITION_ORDER,
    orderMembersByPosition,
} from "../../../utils/memberPositionUtils";
import { buildMembershipMenuItems } from "../../../utils/membershipMenuItems";

// ===== スタイル定義 =====

const UserListContainer = styled.div`
  position: relative;
`;

// アバタースタックを縦に並べるコンテナ。空グループ（null）には gap が付かない。
const StackGroups = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DetailsList = styled(motion.div)`
  position: absolute;
  top: 100%;
  right: -12px;
  /* .u-popover-width 相当 */
  width: clamp(320px, 38.2vw, calc(1140px * 0.382));
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: -40px;
  padding: 40px 12px 24px;
  background: #FFF;
  color: #000;
  border-radius: 12px;
  box-shadow: 2px 4px 8px 0px rgba(0, 0, 0, 0.05);
  z-index: 5;
  /* メンバー数が多くてビューポートを超える場合はパネル内でスクロール可能にする。
     framer-motion の height: auto 展開と組み合わせて、自然な高さで上限を打つ。 */
  max-height: calc(100dvh - 120px);
  overflow-y: auto;
  overflow-x: hidden;

  && div {
    color: #000;
  }
  && .u-text-white {
    color: #fff;
  }
  && .u-text-light-gray {
    color: var(--soremo-light-gray);
  }
  && div.material-symbols-rounded {
    color: var(--soremo-light-gray);
  }
`;

const UserItem = styled.div.attrs({
    className: "u-row-between u-gap16",
})`
  padding: 8px;
  background: #fff;
  border-radius: 4px;
  width: 100%;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const UserActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InviteButton = styled.button.attrs({
    className: "u-w100 u-row-end u-gap8",
})`
  padding: 8px 8px;
  border-radius: 4px;
  border: none;
  background: transparent;
  color: #FFF;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
  
  .material-symbols-rounded {
    font-size: 24px;
    color: #FFF;
  }
`;

const InviteActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid #f0f0f0;
  background: #fcfcfc;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    background: #f0f0f0;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  .material-symbols-rounded {
    font-size: 16px;
  }
`;

const FormContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

// ===== コンポーネント =====

/**
 * メンバー配列を共通 `CollapsedAvatarStack` の items 形式へ変換する。
 * オンライン判定はスタック側が presence ストアを内部参照する。
 */
const toStackItems = (members) =>
    members.map((m) => ({
        key: m._key,
        userId: m.user_id,
        avatarUrl: m.avatar_url,
        displayName: m.display_name_jp,
    }));

const UserList = ({
    projectId: projectIdProp,
    memberSummaries,
    currentUserRole,
    /**
     * プロジェクト一覧カード向けのコンパクト表示モード。有効にすると:
     *   - クリックしても詳細リスト（DetailsList）を開かない
     *   - アバタースタックをクライアント / プロデューサー / スペシャリストの
     *     3 グループに行で分けて表示する（ユーザーリストと並び順を揃える）
     * バナー（無効時）はクライアント＋プロデューサーを 1 行にまとめて表示する。
     */
    disableDetails = false,
    /**
     * disableDetails 中でもバナーと同じ 2 段構成にする:
     *   1 段目 = クライアント＋プロデューサー / 2 段目 = スペシャリスト。
     * プロジェクトリストのホイール詳細パネル（ProjectWheel）で使用。
     */
    combineMainGroups = false,
} = {}) => {
    const { projectId: routeProjectId } = useParams();
    const projectId = projectIdProp || routeProjectId;
    const dispatch = useDispatch();

    const {
        data: currentUserData,
        isLoading: isLoadingUser,
    } = useGetCurrentUserQuery();

    // オンライン中のユーザー ID 一覧（WebSocket `/ws/spa/presence` から購読）
    const onlineUserIds = useSelector((state) => state.presence.onlineUserIds);
    const isUserOnline = (userId) => !!userId && onlineUserIds.includes(userId);

    // memberSummaries が渡される（プロジェクト一覧）ときは詳細を開くまでフル fetch を抑止する。
    const [isOpen, setIsOpen] = useState(false);
    const hasPreloadedSummaries = Array.isArray(memberSummaries) && memberSummaries.length > 0;
    const shouldFetchFull = !hasPreloadedSummaries || isOpen;

    const {
        data: myMembershipData,
    } = useGetProjectMembershipQuery(projectId || "", {
        skip: !projectId || isLoadingUser || !currentUserData || !shouldFetchFull,
    });

    const {
        data: membershipsData,
        isLoading: isLoadingMemberships,
    } = useGetAllProjectMembershipsQuery(
        { projectId },
        { skip: !projectId || isLoadingUser || !currentUserData || !shouldFetchFull }
    );

    const currentUserPosition =
        myMembershipData?.data?.position?.toUpperCase()
        || (currentUserRole ? String(currentUserRole).toUpperCase() : "");
    const currentUserId = currentUserData?.data?.id;
    const isPrimaryClient = currentUserPosition === "CLIENT";
    const isPrimaryProducer = currentUserPosition === "PRODUCER";
    const hasControl = canControlMembers(currentUserPosition);

    // 招待一覧を取得（招待中ユーザー表示用）。詳細を開くまではフェッチしない。
    const {
        data: invitationsData,
    } = useGetInvitationsQuery(projectId, {
        skip: !projectId || !hasControl || !shouldFetchFull,
    });

    const [sendInvitation, { isLoading: isSendingInvite }] = useSendInvitationMutation();
    const [resendInvitation] = useResendInvitationMutation();
    const [cancelInvitation] = useCancelInvitationMutation();
    const [updateMembership] = useUpdateMembershipMutation();
    const [removeMembership] = useRemoveMembershipMutation();

    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
    const [inviteError, setInviteError] = useState("");
    const [sendStatus, setSendStatus] = useState("idle");
    const [listStatus, setListStatus] = useState({ status: "idle", message: "" });
    const listStatusTimer = useRef(null);
    const [clientInviteForm, setClientInviteForm] = useState({
        email: "",
        ipRestriction: "",
        isPrimaryClient: false,
    });
    const wrapperRef = useRef(null);
    const [clientMembers, setClientMembers] = useState([]);
    const [producerMembers, setProducerMembers] = useState([]);
    const [specialistMembers, setSpecialistMembers] = useState([]);
    const [ipRestrictionTarget, setIpRestrictionTarget] = useState(null);
    const [removeMemberTarget, setRemoveMemberTarget] = useState(null);
    const [isRemoving, setIsRemoving] = useState(false);

    const showListStatus = (status, message) => {
        if (listStatusTimer.current) clearTimeout(listStatusTimer.current);
        setListStatus({ status, message });
        if (status === "saved") {
            listStatusTimer.current = setTimeout(() => {
                setListStatus({ status: "idle", message: "" });
            }, 2000);
        }
    };

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    useEffect(() => {
        if (!membershipsData?.data) return;
        const members = membershipsData.data.map((m, index) => ({
            ...m,
            _key: m.user_id ? String(m.user_id) : `member-${index}`,
        }));

        // 招待レコードをメンバー互換オブジェクトに変換
        const pendingInvitations = (invitationsData?.data || []).map((inv) => ({
            _key: `invitation-${inv.user_id}`,
            _invitationId: inv.user_id,
            is_invited: true,
            email: inv.email,
            position: inv.position,
            display_name_jp: inv.display_name,
            avatar_url: inv.avatar_url,
        }));

        const primaryClients = members.filter(m => m.position === "CLIENT");
        const normalClients = members.filter(m => m.position === "REVIEWER");
        const invitedClients = pendingInvitations.filter(m => isClientGroup(m.position));
        setClientMembers([...primaryClients, ...normalClients, ...invitedClients]);

        const primaryProducers = members.filter(m => m.position === "PRODUCER");
        const normalProducers = members.filter(m => m.position === "DIRECTOR");
        const adminProducers = members.filter(m => m.position === "ADMIN");
        const invitedProducers = pendingInvitations.filter(m => isProducerGroup(m.position));
        setProducerMembers([...primaryProducers, ...normalProducers, ...adminProducers, ...invitedProducers]);

        const specialists = members.filter(m => isSpecialistPosition(m.position));
        const invitedSpecialists = pendingInvitations.filter(m => isSpecialistPosition(m.position));
        setSpecialistMembers([...specialists, ...invitedSpecialists]);
    }, [membershipsData, invitationsData]);

    const showSpecialists = shouldShowSpecialists(currentUserPosition);

    // ===== アバタースタック用の表示メンバー（招待中を除く）=====
    // 詳細パネル（ユーザーリスト）と並び順を揃えるため、グループごとに分割して保持する。
    // フル fetch 済みなら membershipsData 由来の整列済み配列を、未 fetch（プロジェクト一覧）なら
    // member_summaries を同じグループ順に並べ替えて使う。

    const visibleClientMembers = useMemo(() => {
        if (membershipsData?.data) {
            return clientMembers.filter(m => !m.is_invited);
        }
        if (hasPreloadedSummaries) {
            const list = memberSummaries
                .filter(m => !m.is_invited && isClientGroup(m.position))
                .map((m, index) => ({
                    ...m,
                    _key: m.user_id ? String(m.user_id) : `summary-client-${index}`,
                }));
            return orderMembersByPosition(list, CLIENT_GROUP_POSITION_ORDER);
        }
        return [];
    }, [membershipsData, clientMembers, hasPreloadedSummaries, memberSummaries]);

    const visibleProducerMembers = useMemo(() => {
        if (membershipsData?.data) {
            return producerMembers.filter(m => !m.is_invited);
        }
        if (hasPreloadedSummaries) {
            const list = memberSummaries
                .filter(m => !m.is_invited && isProducerGroup(m.position))
                .map((m, index) => ({
                    ...m,
                    _key: m.user_id ? String(m.user_id) : `summary-producer-${index}`,
                }));
            return orderMembersByPosition(list, PRODUCER_GROUP_POSITION_ORDER);
        }
        return [];
    }, [membershipsData, producerMembers, hasPreloadedSummaries, memberSummaries]);

    const visibleSpecialistMembers = useMemo(() => {
        if (membershipsData?.data) {
            return specialistMembers.filter(m => !m.is_invited);
        }
        if (hasPreloadedSummaries) {
            return memberSummaries
                .filter(m => !m.is_invited && isSpecialistPosition(m.position))
                .map((m, index) => ({
                    ...m,
                    _key: m.user_id ? String(m.user_id) : `summary-specialist-${index}`,
                }));
        }
        return [];
    }, [membershipsData, specialistMembers, hasPreloadedSummaries, memberSummaries]);

    // バナー（詳細パネル有効時）はクライアント＋プロデューサーを 1 行にまとめて表示する。
    const visibleMainMembers = useMemo(
        () => [...visibleClientMembers, ...visibleProducerMembers],
        [visibleClientMembers, visibleProducerMembers]
    );

    const handleDragEnd = async (event, groupType) => {
        const { active, over } = event;
        
        if (!over || active.id === over.id) {
            return;
        }

        const members = groupType === 'client' ? clientMembers : producerMembers;
        const setMembers = groupType === 'client' ? setClientMembers : setProducerMembers;
        const primaryPos = groupType === 'client' ? "CLIENT" : "PRODUCER";

        const oldIndex = members.findIndex(m => m._key === active.id);
        const newIndex = members.findIndex(m => m._key === over.id);

        const reorderedMembers = arrayMove(members, oldIndex, newIndex);

        // ロール内での並び替え: 責任者 → 一般 → 招待中の順
        const primaryMembers = reorderedMembers.filter(m => m.position === primaryPos && !m.is_invited);
        const normalMembers = reorderedMembers.filter(m => m.position !== primaryPos && !m.is_invited);
        const invitedMembers = reorderedMembers.filter(m => m.is_invited);
        const finalMembers = [...primaryMembers, ...normalMembers, ...invitedMembers];

        const previousMembers = [...members];
        setMembers(finalMembers);

        // order_user をAPIに保存（変更があったメンバーのみ）
        const updates = finalMembers
            .map((m, index) => ({ ...m, newOrder: index + 1 }))
            .filter((m) => m.order_user !== m.newOrder && m.user_id);

        if (updates.length === 0) return;

        showListStatus("saving", "保存中...");
        try {
            await Promise.all(
                updates.map((m) =>
                    updateMembership({
                        projectId,
                        userId: m.user_id,
                        order_user: m.newOrder,
                    }).unwrap()
                )
            );
            showListStatus("saved", "保存済み");
        } catch (err) {
            setMembers(previousMembers);
            showListStatus("error", "保存に失敗しました");
            console.error("order_user の更新に失敗:", err);
        }
    };

    // ===== 招待操作ハンドラ =====

    const handleResendInvitation = async (membership) => {
        const invitationId = membership._invitationId || membership.user_id;
        showListStatus("saving", "再送信中...");
        try {
            await resendInvitation({ projectId, userId: invitationId }).unwrap();
            showListStatus("saved", "再送信しました");
        } catch (err) {
            showListStatus("error", "再送信に失敗しました");
            console.error("招待の再送信に失敗:", err);
        }
    };

    const handleCancelInvitation = async (membership) => {
        const invitationId = membership._invitationId || membership.user_id;
        showListStatus("saving", "取り消し中...");
        try {
            await cancelInvitation({ projectId, userId: invitationId }).unwrap();
            dispatch(membershipsApi.util.invalidateTags([{ type: "MembershipsList", id: projectId }]));
            showListStatus("saved", "招待を取り消しました");
        } catch (err) {
            showListStatus("error", "取り消しに失敗しました");
            console.error("招待のキャンセルに失敗:", err);
        }
    };

    // ===== メンバーシップ操作ハンドラ =====

    const handleUpdatePosition = async (membership, newPosition) => {
        showListStatus("saving", "更新中...");
        try {
            await updateMembership({
                projectId,
                userId: membership.user_id,
                position: newPosition,
            }).unwrap();
            showListStatus("saved", "更新しました");
        } catch (err) {
            showListStatus("error", "更新に失敗しました");
            console.error("ポジション更新に失敗:", err);
        }
    };

    const handleRemoveMember = (membership) => {
        setRemoveMemberTarget(membership);
    };

    const confirmRemoveMember = async () => {
        if (!removeMemberTarget) return;
        setIsRemoving(true);
        showListStatus("deleting", "");
        try {
            await removeMembership({
                projectId,
                userId: removeMemberTarget.user_id,
            }).unwrap();
            showListStatus("saved", "退出しました");
            setRemoveMemberTarget(null);
        } catch (err) {
            showListStatus("error", "退出に失敗しました");
            console.error("メンバーシップ削除に失敗:", err);
        } finally {
            setIsRemoving(false);
        }
    };

    const cancelRemoveMember = () => {
        setRemoveMemberTarget(null);
    };

    // ===== メニュー項目生成 =====

    // 「最後の責任者は解除 / 退出不可」ガードに使う責任者数（招待中を除く）。
    const primaryClientCount = useMemo(
        () => clientMembers.filter(m => m.position === "CLIENT" && !m.is_invited).length,
        [clientMembers]
    );
    const primaryProducerCount = useMemo(
        () => producerMembers.filter(m => m.position === "PRODUCER" && !m.is_invited).length,
        [producerMembers]
    );

    // 項目・ガード条件は管理画面のメンバーシップモーダルと共通
    // （utils/membershipMenuItems.ts）。
    const getMenuItems = (membership) => {
        // 操作権限がない場合は空メニュー
        if (!hasControl) return [];

        return buildMembershipMenuItems(membership, {
            // クライアント責任者は自グループのみ、プロデューサー責任者は全グループを操作できる
            canControlClients: isPrimaryClient || isPrimaryProducer,
            canControlProducers: isPrimaryProducer,
            primaryClientCount,
            primaryProducerCount,
            onUpdatePosition: handleUpdatePosition,
            onOpenIpRestriction: handleOpenIpRestrictionModal,
            onRemove: handleRemoveMember,
        });
    };

    // ===== UI操作ハンドラ =====

    const handleToggle = () => {
        if (disableDetails) return;
        setIsOpen(!isOpen);
    };

    // 各アバタースタックに共通で渡す props（クリック挙動・開閉状態）。
    const stackProps = {
        onClick: handleToggle,
        flat: isOpen,
    };

    const handleOpenInviteModal = () => {
        setIsInviteModalOpen(true);
    };

    const handleOpenIpRestrictionModal = (membership) => {
        setIpRestrictionTarget(membership);
    };

    const handleCloseIpRestrictionModal = () => {
        setIpRestrictionTarget(null);
    };

    const handleCloseInviteModal = () => {
        setIsInviteModalOpen(false);
        setInviteError("");
        setSendStatus("idle");
        setClientInviteForm({
            email: "",
            ipRestriction: "",
            isPrimaryClient: false,
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setClientInviteForm({
            ...clientInviteForm,
            [name]: value
        });
    };

    const handleSubmitInvite = async () => {
        setInviteError("");
        setSendStatus("saving");
        try {
            const ipRestrictions = clientInviteForm.ipRestriction
                ? [clientInviteForm.ipRestriction]
                : [];
            await sendInvitation({
                projectId,
                email: clientInviteForm.email,
                ip_restrictions: ipRestrictions,
                is_manager: clientInviteForm.isPrimaryClient,
            }).unwrap();

            dispatch(membershipsApi.util.invalidateTags([{ type: "MembershipsList", id: projectId }]));

            setSendStatus("saved");
            setTimeout(() => {
                handleCloseInviteModal();
            }, 1000);
        } catch (err) {
            setSendStatus("error");
            const message = err?.data?.message || err?.data?.detail || "招待の送信に失敗しました。";
            setInviteError(message);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (ipRestrictionTarget) return;
            if (event.target.closest('[data-tippy-root]')) return;
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [ipRestrictionTarget]);

    useEffect(() => {
        return () => {
            if (listStatusTimer.current) clearTimeout(listStatusTimer.current);
        };
    }, []);

    // ===== 非ドラッグ用のユーザーアイテム描画（閲覧専用ユーザー向け） =====

    const renderUserItem = (membership) => {
        const { position, is_invited } = membership;
        const isPrimary = isPrimaryPosition(position);
        const menuItems = getMenuItems(membership);
        
        if (is_invited) {
            return (
                <UserItem key={membership._key}>
                    <div className="u-row u-gap16">
                        <div>
                            <StatusBadge 
                                label="招待中" 
                                color="border" 
                            />
                        </div>
                        <UserInfo>
                            <div className="bodytext-11 u-text-light-gray u-line-height-100">{membership.email || "招待中メールアドレス"}</div>
                        </UserInfo>
                    </div>
                    {hasControl && (
                        <UserActions>
                            <InviteActionButton onClick={() => handleResendInvitation(membership)}>
                                <span className="material-symbols-rounded">send</span>
                                <span className="bodytext-11">再送信</span>
                            </InviteActionButton>
                            <InviteActionButton onClick={() => handleCancelInvitation(membership)}>
                                <span className="material-symbols-rounded">close</span>
                                <span className="bodytext-11">取り消し</span>
                            </InviteActionButton>
                        </UserActions>
                    )}
                </UserItem>
            );
        }

        const showFollow = isFollowableMember(membership, currentUserId);
        const showMenu = hasControl && menuItems.length > 0;

        return (
            <UserListRow
                key={membership._key}
                variant="flat"
                user={{
                    avatar_url: membership.avatar_url,
                    display_name: membership.display_name_jp,
                    title: `${membership.title_jp || ""}${isPrimary ? "（責任者）" : ""}`,
                    affiliation: isPrimary ? membership.affiliation_jp : undefined,
                }}
                avatarSize={isPrimary ? 48 : 32}
                isOnline={isUserOnline(membership.user_id)}
                leadingSlot={!isPrimary ? <MemberIndentSpacer /> : undefined}
                trailingSlot={
                    showFollow || showMenu ? (
                        <UserActions>
                            {showFollow && (
                                <MemberFollowButton
                                    userId={membership.user_id}
                                    initialIsFollowing={!!membership.is_following}
                                />
                            )}
                            {showMenu && <DropdownMenu items={menuItems} />}
                        </UserActions>
                    ) : undefined
                }
            />
        );
    };

    // 一覧画面ではサマリだけで先行描画する。それ以外は従来どおりフル fetch を待つ。
    if (!hasPreloadedSummaries && (isLoadingMemberships || !membershipsData)) {
        return null;
    }

    return (
        <UserListContainer ref={wrapperRef}>
            {/* アバタースタック表示 */}
            <StackGroups>
                {disableDetails && !combineMainGroups ? (
                    // プロジェクト一覧カード: クライアント / プロデューサーを別の行に分ける
                    <>
                        <CollapsedAvatarStack items={toStackItems(visibleClientMembers)} {...stackProps} />
                        <CollapsedAvatarStack items={toStackItems(visibleProducerMembers)} {...stackProps} />
                    </>
                ) : (
                    // バナー / ホイール詳細: クライアント＋プロデューサーを 1 行にまとめる
                    <CollapsedAvatarStack items={toStackItems(visibleMainMembers)} {...stackProps} />
                )}
                {/* スペシャリストグループ（クライアントには非開示） */}
                {showSpecialists && (
                    <CollapsedAvatarStack items={toStackItems(visibleSpecialistMembers)} {...stackProps} />
                )}
            </StackGroups>

            {/* 詳細リスト表示 */}
            <AnimatePresence>
                {isOpen && (
                    <DetailsList
                        initial={{ height: 0, opacity: 0, y: 20 }}
                        animate={{ height: "auto", opacity: 1, y: 0 }}
                        exit={{ height: 0, opacity: 0, y: -20 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                    >
                        {/* クライアントグループ */}
                        <MemberGroupSection
                            variant="client"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ delay: 0.1, duration: 0.3 }}
                        >
                            {hasControl ? (
                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    modifiers={[restrictToVerticalAxis]}
                                    onDragEnd={(event) => handleDragEnd(event, 'client')}
                                >
                                    <SortableContext
                                        items={clientMembers.map(m => m._key)}
                                        strategy={verticalListSortingStrategy}
                                    >
                                        {clientMembers.map(m => (
                                            <SortableUserItem
                                                key={m._key}
                                                membership={m}
                                                currentUserId={currentUserId}
                                                getMenuItems={getMenuItems}
                                                canControl={hasControl}
                                                onResendInvitation={handleResendInvitation}
                                                onCancelInvitation={handleCancelInvitation}
                                            />
                                        ))}
                                    </SortableContext>
                                </DndContext>
                            ) : (
                                clientMembers.map(m => renderUserItem(m))
                            )}
                            
                            {/* クライアント招待ボタン - 責任者のみ表示 */}
                            {hasControl && (
                                <InviteButton onClick={handleOpenInviteModal}>
                                    <span className="bodytext-13">クライアントを招待</span>
                                    <span className="material-symbols-rounded">chevron_right</span>
                                </InviteButton>
                            )}
                        </MemberGroupSection>

                        {/* プロデューサーグループ */}
                        {(producerMembers.length > 0) && (
                            <MemberGroupSection
                                variant="producer"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ delay: 0.2, duration: 0.3 }}
                            >
                                {isPrimaryProducer ? (
                                    <DndContext
                                        sensors={sensors}
                                        collisionDetection={closestCenter}
                                        modifiers={[restrictToVerticalAxis]}
                                        onDragEnd={(event) => handleDragEnd(event, 'producer')}
                                    >
                                        <SortableContext
                                            items={producerMembers.map(m => m._key)}
                                            strategy={verticalListSortingStrategy}
                                        >
                                            {producerMembers.map(m => (
                                                <SortableUserItem
                                                    key={m._key}
                                                    membership={m}
                                                    currentUserId={currentUserId}
                                                    getMenuItems={getMenuItems}
                                                    canControl={isPrimaryProducer}
                                                    onResendInvitation={handleResendInvitation}
                                                    onCancelInvitation={handleCancelInvitation}
                                                />
                                            ))}
                                        </SortableContext>
                                    </DndContext>
                                ) : (
                                    producerMembers.map(m => renderUserItem(m))
                                )}
                            </MemberGroupSection>
                        )}
                        {/* スペシャリストグループ（PRODUCER / DIRECTOR のみ表示） */}
                        {showSpecialists && specialistMembers.length > 0 && (
                            <MemberGroupSection
                                variant="specialist"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ delay: 0.3, duration: 0.3 }}
                            >
                                {specialistMembers.map(m => renderUserItem(m))}
                            </MemberGroupSection>
                        )}
                        {listStatus.status !== "idle" && (
                            <StatusIndicator status={listStatus.status}>
                                {listStatus.message}
                            </StatusIndicator>
                        )}
                    </DetailsList>
                )}
            </AnimatePresence>
            
            {/* クライアント招待モーダル */}
            {isInviteModalOpen && (
                <Modal
                    onClose={handleCloseInviteModal}
                    title="クライアントを招待"
                    size="default"
                    primaryAction={{
                        label: "招待メールを送信",
                        onClick: handleSubmitInvite,
                        disabled: !clientInviteForm.email,
                        loading: isSendingInvite,
                        rightIcon: "send",
                    }}
                >
                    <FormContainer>
                        {inviteError && (
                            <div className="bodytext-13" style={{ color: "var(--soremo-green)", padding: "8px 12px", borderRadius: 4 }}>
                                {inviteError}
                            </div>
                        )}
                        <Input
                            label="メールアドレス"
                            option="required"
                            type="email"
                            name="email"
                            value={clientInviteForm.email}
                            onChange={handleInputChange}
                            placeholder="example@mail.com"
                            inputmode="email"
                        />

                        <Input
                            label="固定IP制限"
                            option="optional"
                            type="text"
                            name="ipRestriction"
                            value={clientInviteForm.ipRestriction}
                            onChange={handleInputChange}
                            placeholder="192.168.1.1"
                        />

                        <Toggle
                            value={clientInviteForm.isPrimaryClient}
                            onClick={() => setClientInviteForm({ ...clientInviteForm, isPrimaryClient: !clientInviteForm.isPrimaryClient })}
                        >
                            <div className="heading-13">責任者として招待<span className="bodytext-13">（メンバー管理・契約手続き可）</span></div>
                        </Toggle>
                    </FormContainer>
                    <StatusIndicator status={sendStatus} inModal>
                        {sendStatus === "saving"
                            ? "送信中..."
                            : sendStatus === "saved"
                                ? "送信完了"
                                : "送信に失敗しました"}
                    </StatusIndicator>
                </Modal>
            )}

            {/* IP制限モーダル */}
            {ipRestrictionTarget && (
                <IpRestrictionModal
                    membership={ipRestrictionTarget}
                    projectId={projectId}
                    onClose={handleCloseIpRestrictionModal}
                />
            )}

            {/* 退出確認モーダル */}
            {removeMemberTarget && (
                <ConfirmModal
                    message="このプロジェクトから退出しますか？"
                    confirmLabel="退出"
                    loading={isRemoving}
                    onConfirm={confirmRemoveMember}
                    onCancel={cancelRemoveMember}
                />
            )}
        </UserListContainer>
    );
};

export default React.memo(UserList);
